import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Scale, Plus, X, ShieldAlert, Loader2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import CultivarPicker, { type PickedCultivar } from '../../components/orchard/CultivarPicker';
import OrgPicker from '../../components/cultivar/OrgPicker';
import upstreamService from '../../services/upstream';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import { money } from '../../utils/nurseryMoney';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import type { UpstreamAgreement, UpstreamOpenAccruals, UpstreamStatementSummary } from '../../types/upstream';

/**
 * /cultivar/upstream — what the organisation owes the breeder or master
 * licensor for the cultivars it sells (backend upstreamAgreementService).
 * GRIP charges a program access fee but does not own the IP; every tree it
 * sells accrues what is owed upstream beside what it collected, and each
 * month's statement is frozen. Nothing here bills anyone.
 *
 * Read with a Cultivar PBR/Admin seat or any Accounts seat; changed with the
 * Cultivar Admin or Accounts Admin seat.
 */

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const rate = (v: number) => `$${Number(v).toFixed(Number(v) % 1 && Number(v) * 100 % 1 ? 4 : 2)}`;

function lastMonth(): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function NewAgreement({ onMade, onCancel }: { onMade: () => void; onCancel: () => void }) {
  const [payeeName, setPayeeName] = useState('');
  const [payeeOrg, setPayeeOrg] = useState<{ id: number; name: string } | null>(null);
  const [payeeEmail, setPayeeEmail] = useState('');
  const [reference, setReference] = useState('');
  const [cultivars, setCultivars] = useState<PickedCultivar[]>([]);
  const [startsOn, setStartsOn] = useState('');
  const [annualMonth, setAnnualMonth] = useState(7);
  const [perSold, setPerSold] = useState('');
  const [perYear, setPerYear] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setError(null);
    const name = payeeName.trim() || payeeOrg?.name || '';
    if (!name) return setError('Name who is owed');
    if (!cultivars.length) return setError('Choose at least one cultivar');
    if (!startsOn) return setError('Give the date the agreement starts');
    setBusy(true);
    try {
      const res = await upstreamService.createAgreement({
        payee_org_id: payeeOrg?.id ?? null, payee_name: name, payee_email: payeeEmail.trim() || null,
        reference: reference.trim() || null, cultivar_ids: cultivars.map((c) => c.id), starts_on: startsOn,
        annual_month: annualMonth, per_tree_sold: Number(perSold) || 0, per_tree_per_year: Number(perYear) || 0,
      });
      if (res.success) onMade();
      else setError(errorMessage(res, 'The agreement could not be saved'));
    } catch (e) {
      setError(errorMessage(e, 'The agreement could not be saved'));
    }
    setBusy(false);
  };

  return (
    <Card>
      <CardHeader><CardTitle>New upstream agreement</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {error && <div className="bg-red-50 border border-red-200 rounded px-3 py-2 text-sm text-red-700 break-words">{error}</div>}
        <div className="space-y-1">
          <p className="text-sm font-semibold text-neutral-700">Who is owed, if they are on FactreeTech</p>
          <p className="text-xs text-neutral-500">They then see what they are owed, totalled by cultivar and month: never your customers or prices.</p>
          <OrgPicker value={payeeOrg} onChange={(o) => { setPayeeOrg(o); if (o && !payeeName) setPayeeName(o.name); }} placeholder="Search organisations…" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Name on the statement *" aria-label="Name on the statement" value={payeeName} onChange={(e) => setPayeeName(e.target.value)} placeholder="e.g. Polar Breeding Pty Ltd" />
          <Input label="Their email" aria-label="Their email" type="email" value={payeeEmail} onChange={(e) => setPayeeEmail(e.target.value)} />
          <Input label="Agreement reference" aria-label="Agreement reference" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. MLA-2026-04" />
          <Input label="Starts on *" aria-label="Starts on" type="date" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} />
        </div>
        <div className="space-y-2">
          <CultivarPicker label="Cultivars it covers *" hint="A tree counts when its variety or its rootstock is one of these."
            value={null} onChange={(c) => { if (c && !cultivars.some((x) => x.id === c.id)) setCultivars([...cultivars, c]); }} />
          {cultivars.length > 0 && (
            <ul className="flex flex-wrap gap-2">
              {cultivars.map((c) => (
                <li key={c.id} className="flex items-center gap-1 bg-neutral-100 rounded-full pl-3 pr-1 py-1 text-sm max-w-full">
                  <span className="break-words min-w-0">{c.name}</span>
                  <button type="button" aria-label={`Remove ${c.name}`} className="p-1 text-neutral-500 hover:text-neutral-800"
                    onClick={() => setCultivars(cultivars.filter((x) => x.id !== c.id))}><X className="h-3.5 w-3.5" /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input label="Owed per tree sold ($)" aria-label="Owed per tree sold" type="number" min="0" step="0.0001" value={perSold} onChange={(e) => setPerSold(e.target.value)} />
          <Input label="Owed per tree per year ($)" aria-label="Owed per tree per year" type="number" min="0" step="0.0001" value={perYear} onChange={(e) => setPerYear(e.target.value)} />
          <div className="space-y-1">
            <label htmlFor="up-annual" className="block text-sm font-semibold text-neutral-700">Yearly amount falls in</label>
            <select id="up-annual" value={annualMonth} onChange={(e) => setAnnualMonth(Number(e.target.value))}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </div>
        </div>
        <p className="text-xs text-neutral-500 break-words">
          Per tree per year counts the trees on your active orchard planting licences for these cultivars, on the 1st of that month.
          A change of rate later is added from its date, so nothing already worked out moves.
        </p>
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save agreement'}</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function AgreementCard({ a, canAdmin, open, statements, onChanged }: {
  a: UpstreamAgreement; canAdmin: boolean; open: UpstreamOpenAccruals | null;
  statements: UpstreamStatementSummary[]; onChanged: () => void;
}) {
  const tz = useOrgTimezone();
  const [rateFrom, setRateFrom] = useState('');
  const [rateSold, setRateSold] = useState('');
  const [rateYear, setRateYear] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showRate, setShowRate] = useState(false);
  const accruing = (open?.rows || []).filter((r) => r.agreement_id === a.id);
  const mine = statements.filter((s) => s.agreement_id === a.id);

  const act = async (fn: () => Promise<{ success: boolean }>, failMsg: string) => {
    setBusy(true); setError(null);
    try {
      const res = await fn();
      if (res.success) { setShowRate(false); onChanged(); } else setError(errorMessage(res, failMsg));
    } catch (e) {
      setError(errorMessage(e, failMsg));
    }
    setBusy(false);
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-semibold text-neutral-900 break-words">
              {a.payee_name}{!a.active && <span className="ml-2 text-xs font-normal text-neutral-500">(switched off)</span>}
            </h3>
            <p className="text-xs text-neutral-500 break-words">
              {a.reference ? `${a.reference} · ` : ''}From {formatLocalDateInTz(a.starts_on.slice(0, 10), tz)}
              {a.ends_on ? ` to ${formatLocalDateInTz(a.ends_on.slice(0, 10), tz)}` : ''}
              {a.payee_org_name ? ` · sees its figures on FactreeTech` : ''}
            </p>
          </div>
          <div className="text-right text-sm">
            <div>Owed on statements: <strong>{money(a.owed_on_statements)}</strong></div>
            <div className="text-neutral-600">This month so far: {money(a.unstated_amount)}</div>
          </div>
        </div>
        <p className="text-sm text-neutral-700 break-words">{a.cultivars.map((c) => c.trade_name || c.name).join(', ')}</p>
        {a.current_rate && (
          <p className="text-sm text-neutral-700">
            {rate(a.current_rate.per_tree_sold)} per tree sold · {rate(a.current_rate.per_tree_per_year)} per tree per year, in {MONTHS[a.annual_month - 1]}
          </p>
        )}
        {accruing.length > 0 && (
          <ul className="text-xs text-neutral-600 space-y-0.5">
            {accruing.map((r) => (
              <li key={`${r.agreement_id}-${r.cultivar_name}`} className="break-words">
                {r.cultivar_name}: {r.trees_sold} sold{r.trees_annual ? `, ${r.trees_annual} licensed` : ''} → {money(r.amount)} owed (collected {money(r.collected)})
              </li>
            ))}
          </ul>
        )}
        {mine.length > 0 && (
          <details className="text-sm">
            <summary className="cursor-pointer text-neutral-600">Statements ({mine.length})</summary>
            <ul className="mt-1 divide-y divide-neutral-100">
              {mine.map((s) => (
                <li key={s.id} className="py-1.5 flex flex-wrap items-center justify-between gap-2">
                  <Link to={`/cultivar/upstream/statements/${s.id}`} className="text-primary-700 hover:underline">
                    {MONTHS[Number(s.period_to.slice(5, 7)) - 1]} {s.period_to.slice(0, 4)}
                  </Link>
                  <span className="text-neutral-700">{money(s.amount)}</span>
                  <span className={`text-xs ${s.paid_at ? 'text-green-800' : 'text-amber-800'}`}>
                    {s.paid_at ? `Paid ${formatLocalDateInTz(s.paid_at.slice(0, 10), tz)}` : 'Not paid'}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}
        {error && <div className="bg-red-50 border border-red-200 rounded px-3 py-2 text-sm text-red-700 break-words">{error}</div>}
        {canAdmin && (
          <div className="space-y-2">
            {showRate ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
                <Input label="New rate from" aria-label="New rate from" type="date" value={rateFrom} onChange={(e) => setRateFrom(e.target.value)} />
                <Input label="Per tree sold ($)" aria-label="New per tree sold" type="number" min="0" step="0.0001" value={rateSold} onChange={(e) => setRateSold(e.target.value)} />
                <Input label="Per tree per year ($)" aria-label="New per tree per year" type="number" min="0" step="0.0001" value={rateYear} onChange={(e) => setRateYear(e.target.value)} />
                <div className="sm:col-span-3 flex flex-wrap gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={() => setShowRate(false)} disabled={busy}>Cancel</Button>
                  <Button size="sm" disabled={busy || !rateFrom}
                    onClick={() => act(() => upstreamService.addRate(a.id, { effective_from: rateFrom, per_tree_sold: Number(rateSold) || 0, per_tree_per_year: Number(rateYear) || 0 }), 'The rate could not be added')}>
                    Add rate
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowRate(true)}>New rate from a date</Button>
                <Button variant="outline" size="sm" disabled={busy}
                  onClick={() => act(() => upstreamService.updateAgreement(a.id, { active: !a.active }), 'The agreement could not be changed')}>
                  {a.active ? 'Switch off' : 'Switch on'}
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function UpstreamAgreements() {
  const cultivarSeat = useModuleLicense('cultivar', ['pbr', 'admin']);
  const accountsSeat = useModuleLicense('accounts', ['user', 'admin']);
  const cultivarAdmin = useModuleLicense('cultivar', 'admin');
  const accountsAdmin = useModuleLicense('accounts', 'admin');
  const canRead = cultivarSeat.hasAccess || accountsSeat.hasAccess;
  const canAdmin = cultivarAdmin.hasAccess || accountsAdmin.hasAccess;
  const seatsLoading = cultivarSeat.loading || accountsSeat.loading;

  const [agreements, setAgreements] = useState<UpstreamAgreement[]>([]);
  const [statements, setStatements] = useState<UpstreamStatementSummary[]>([]);
  const [open, setOpen] = useState<UpstreamOpenAccruals | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [month, setMonth] = useState(lastMonth);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [a, s, o] = await Promise.all([upstreamService.listAgreements(), upstreamService.listStatements(), upstreamService.openAccruals()]);
      if (a.success && a.data) setAgreements(a.data); else setError(errorMessage(a, 'The agreements could not be read'));
      if (s.success && s.data) setStatements(s.data);
      if (o.success && o.data) setOpen(o.data);
    } catch (e) {
      setError(errorMessage(e, 'The agreements could not be read'));
    }
    setLoading(false);
  }, []);

  useEffect(() => { if (canRead) load(); }, [canRead, load]);

  const makeMonth = async () => {
    setNotice(null); setError(null);
    try {
      const res = await upstreamService.makeStatements(month);
      if (res.success && res.data) {
        const n = res.data.made.length;
        setNotice(n ? `${n} statement${n === 1 ? '' : 's'} made.` : 'Nothing to make: every agreement already has that month.');
        load();
      } else setError(errorMessage(res, 'The statements could not be made'));
    } catch (e) {
      setError(errorMessage(e, 'The statements could not be made'));
    }
  };

  if (seatsLoading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-neutral-400" /></div>;
  if (!canRead) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card><CardContent className="p-6 flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <h3 className="font-semibold mb-1">Cultivar PBR or Admin seat, or an Accounts seat, required</h3>
            <p className="text-sm text-gray-600">What is owed upstream is kept by those seats.</p>
          </div>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <>
      <PageHeader title="Owed upstream" subtitle="What you owe breeders and master licensors for the cultivars you sell"
        icon={<Scale className="h-6 w-6 text-white" />} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        <section className="bg-white border border-neutral-200 rounded-xl p-4 text-sm text-neutral-700 space-y-2 break-words">
          <p>
            Every tree on your invoices whose variety or rootstock is covered by an agreement records what you owe for it,
            beside what you charged. Trees credited back, and invoices voided, come back off. Each month's statement is made on
            the 1st and frozen; mark it paid when you pay it. Nothing here is billed or sent for you.
          </p>
        </section>
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}
        {notice && <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-800 break-words">{notice}</div>}

        {canAdmin && !adding && (
          <div className="flex flex-wrap items-end justify-between gap-3">
            <Button onClick={() => setAdding(true)}><Plus className="h-4 w-4 mr-1" /> New agreement</Button>
            <div className="flex flex-wrap items-end gap-2">
              <div className="space-y-1">
                <label htmlFor="up-month" className="block text-xs text-neutral-600">Make an ended month's statements now</label>
                <input id="up-month" type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)}
                  className="rounded-lg border border-neutral-300 px-3 py-2 text-sm" />
              </div>
              <Button variant="outline" onClick={makeMonth}>Make</Button>
            </div>
          </div>
        )}
        {adding && <NewAgreement onCancel={() => setAdding(false)} onMade={() => { setAdding(false); load(); }} />}

        {loading ? (
          <p className="text-sm text-neutral-500">Working out what is owed…</p>
        ) : agreements.length === 0 ? (
          <p className="text-sm text-neutral-500">No agreements yet.</p>
        ) : agreements.map((a) => (
          <AgreementCard key={a.id} a={a} canAdmin={canAdmin} open={open} statements={statements} onChanged={load} />
        ))}
      </div>
    </>
  );
}

export default UpstreamAgreements;
